// Editor surface for a file-backed (upload) working copy. Picks the in-app Word editor
// for .docx, else the replace-file card (PDF/Excel/other). Owns the .docx byte-fetch.
//
// Two different failures, two different answers. If the bytes could not be FETCHED (the server
// shed the request, the stored file is missing, the network dropped) the document itself is
// fine: say what happened and offer to try again — never quietly show the replace card, which
// made a server 500 look like "this file type is replace-only". If the Word editor cannot PARSE
// the file, the file really is one this editor can't open: fall back to replacing it, and say so.
import { toast } from 'sonner';
import { Suspense, lazy, useEffect, useState, type RefObject } from 'react';
import { Button } from '@/components/ui/button';
import { fileKind } from '../lib/fileKind';
import { versionDownloadUrl } from '../collab';
import { previewFailureText } from '../previewError';
import { FileReplaceCard } from './FileReplaceCard';
import type { EditorRail, EditorSaveState } from './docx/DocxEditor';

/** A failed download, carrying the HTTP status so the message can say what actually happened. */
class DownloadFailed extends Error {
  status: number;
  constructor(status: number) {
    super(`download ${status}`);
    this.status = status;
  }
}

const DocxEditorPanel = lazy(() => import('./docx/DocxEditor'));
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export interface FileEditorProps {
  /** Vault document ref (e.g. `upload:42`). */
  refId: string;
  /** The open working-copy version id (for the download URL + byte fetch). */
  versionId: number;
  filename: string | null;
  contentType: string | null;
  /** Display name stamped on comments/tracked changes in the Word editor. */
  authorName?: string;
  /** True while a save is being persisted (drives the replace-card "Replacing…" state). */
  saving: boolean;
  /** Persist a File as the working copy's new bytes (used by both editor surfaces).
   *  Resolves on success / rejects on failure (the Word editor uses this for "Saved"). */
  onSaveFile: (file: File) => void | Promise<void>;
  /** Live autosave state from the Word editor, forwarded up for the sticky save indicator. */
  onSaveStateChange?: (state: EditorSaveState) => void;
  /** Forwarded to the Word editor: set true before a Discard/Submit navigate so the unmount
   *  flush skips (avoids a doomed save + "Save failed" toast against the now-gone draft). */
  skipFinalSaveRef?: RefObject<boolean>;
  /** Forwarded to the Word editor: show the author's own change list beside the document. */
  rail?: EditorRail;
}

function Loader({ label }: { label: string }) {
  return <p className="rounded-2xl border border-line bg-white px-6 py-16 text-center text-[13px] text-muted shadow-card">{label}</p>;
}

export function FileEditor({
  refId,
  versionId,
  filename,
  contentType,
  authorName,
  saving,
  onSaveFile,
  onSaveStateChange,
  skipFinalSaveRef,
  rail,
}: FileEditorProps) {
  const kind = fileKind(contentType, filename);
  const [docxUnparseable, setDocxUnparseable] = useState(false); // the editor could not open it
  const [fetchError, setFetchError] = useState<unknown>(null);   // the bytes never arrived
  const [attempt, setAttempt] = useState(0);
  const [docxBuffer, setDocxBuffer] = useState<ArrayBuffer | null>(null);
  const useDocx = kind === 'docx' && !docxUnparseable;

  // Fetch the working-copy bytes once for the Word editor (drafts aren't cached; the
  // download endpoint serves the file-backed version's stored bytes for the owner).
  useEffect(() => {
    if (!useDocx) return;
    let alive = true;
    setDocxBuffer(null);
    setFetchError(null);
    fetch(versionDownloadUrl(refId, versionId), { credentials: 'same-origin' })
      .then((r) => {
        if (!r.ok) throw new DownloadFailed(r.status);
        return r.arrayBuffer();
      })
      .then((b) => alive && setDocxBuffer(b))
      .catch((err: unknown) => alive && setFetchError(err ?? new Error('download failed')));
    return () => {
      alive = false;
    };
  }, [useDocx, refId, versionId, attempt]);

  if (useDocx) {
    if (fetchError) {
      return (
        <div className="rounded-2xl border border-line bg-white px-6 py-12 text-center shadow-card">
          <p className="text-[13px] text-muted">{previewFailureText(fetchError)}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </Button>
        </div>
      );
    }
    if (!docxBuffer) return <Loader label="Opening the document for editing…" />;
    return (
      <Suspense fallback={<Loader label="Loading the editor…" />}>
        <DocxEditorPanel
          buffer={docxBuffer}
          filename={filename ?? 'document.docx'}
          author={authorName}
          onSave={(bytes) =>
            onSaveFile(new File([bytes], filename ?? 'document.docx', { type: DOCX_MIME }))
          }
          onSaveStateChange={onSaveStateChange}
          onBlocked={(message) => toast.error(message, { duration: 8000 })}
          onLoadError={() => setDocxUnparseable(true)}
          skipFinalSaveRef={skipFinalSaveRef}
          rail={rail}
        />
      </Suspense>
    );
  }

  // PDF / Excel / other — or a .docx the Word editor couldn't open: replace the whole file.
  return (
    <>
      {docxUnparseable && (
        <p className="mb-3 rounded-xl border border-line bg-white px-4 py-3 text-[13px] text-muted shadow-card">
          The in-app editor couldn&apos;t open this Word file, so it can only be replaced here.
          Download it, edit it in Word, and upload the revised file.
        </p>
      )}
    <FileReplaceCard
      filename={filename}
      downloadUrl={versionDownloadUrl(refId, versionId)}
      viewUrl={kind === 'pdf' ? versionDownloadUrl(refId, versionId, true) : null}
      replacing={saving}
      onPick={onSaveFile}
    />
    </>
  );
}
